import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [userToken, setUserToken] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadStoredAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const storedUserData = localStorage.getItem('userData');

        if (isMounted) {
          if (token && storedUserData) {
            setUserToken(token);
            try {
              setUserData(JSON.parse(storedUserData));
            } catch (err) {
              console.error('Error parsing stored user data:', err);
              localStorage.removeItem('userData');
            }
          }
        }
      } catch (error) {
        console.error('Error loading auth state:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadStoredAuth();

    return () => {
      isMounted = false; 
    };
  }, []);

  const signIn = async ({ token, user }) => {
    try {
      // Validate token and user data
      if (typeof token !== 'string') {
        throw new Error('Token must be a string');
      }
      if (!user || typeof user !== 'object') {
        throw new Error('Invalid user data');
      }

      const userString = JSON.stringify(user);

      // Store items sequentially
      localStorage.setItem('token', token);
      localStorage.setItem('userData', userString);

      setUserToken(token);
      setUserData(user);
    } catch (error) {
      console.error('Error storing auth state:', error);
      throw new Error(`Failed to sign in: ${error.message}`);
    }
  };

  const signOut = async () => {
    try {
      // Clear localStorage and state
      localStorage.removeItem('token');
      localStorage.removeItem('userData');

      setUserToken(null);
      setUserData(null);
    } catch (error) {
      console.error('Error during sign out:', error);
      throw new Error('Failed to sign out');
    }
  };

  const updateUserData = async (newData) => {
    try {
      if (!userData) {
        throw new Error('No user data to update');
      }

      const updatedData = { ...userData, ...newData };
      const userString = JSON.stringify(updatedData);

      localStorage.setItem('userData', userString);
      setUserData(updatedData);
    } catch (error) {
      console.error('Error updating user data:', error);
      throw new Error('Failed to update user data');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isAuthenticated: !!userToken,
        userToken,
        userData,
        restaurantId: userData?.restaurantId,
        signIn,
        signOut,
        updateUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;